import React, { useState } from 'react';
import { createUser } from '../../services/api';
import './AddUserModal.css';

const INITIAL_FORM = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'USER',
  enabled: true,
};

export default function AddUserModal({ onClose, onSave }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Client-side validation ─────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.username.trim())
      e.username = 'Username is required';
    else if (form.username.length < 3 || form.username.length > 50)
      e.username = 'Username must be 3–50 characters';
    else if (!/^[a-zA-Z0-9._\-]+$/.test(form.username))
      e.username = 'Only letters, digits, dots, underscores, or hyphens allowed';

    if (!form.email.trim())
      e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Must be a valid email address';
    else if (form.email.length > 100)
      e.email = 'Email must not exceed 100 characters';

    if (!form.password)
      e.password = 'Password is required';
    else if (form.password.length < 8 || form.password.length > 72)
      e.password = 'Password must be 8–72 characters';

    if (form.password !== form.confirmPassword)
      e.confirmPassword = 'Passwords do not match';

    return e;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    // Clear per-field error and any API-level error on edit
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  // ── Form submission — calls the real API ───────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validate();
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    setSubmitting(true);
    setApiError('');

    try {
      const { confirmPassword, ...payload } = form;
      const response = await createUser(payload);
      // Pass the server-returned user object to the parent
      onSave(response.data);
    } catch (err) {
      const msg = err.message || 'Failed to create user. Please try again.';
      // If the conflict is about a specific field, put it on that field
      if (err.status === 409) {
        if (msg.toLowerCase().includes('username')) {
          setErrors(prev => ({ ...prev, username: msg }));
        } else if (msg.toLowerCase().includes('email')) {
          setErrors(prev => ({ ...prev, email: msg }));
        } else {
          setApiError(msg);
        }
      } else {
        setApiError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-group">
            <div className="modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
            </div>
            <div>
              <h2 className="modal-title">Add New User</h2>
              <p className="modal-subtitle">Fill in the details to create a new user account</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit} noValidate>

          {/* API-level error banner */}
          {apiError && (
            <div className="api-error-banner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {apiError}
            </div>
          )}

          <div className="form-row">
            <div className={`form-group ${errors.username ? 'has-error' : ''}`}>
              <label className="form-label">
                Username <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <input
                  className="form-input"
                  type="text"
                  name="username"
                  placeholder="e.g. john.doe"
                  value={form.username}
                  onChange={handleChange}
                  autoComplete="off"
                  disabled={submitting}
                />
                {form.username && <button type="button" className="input-clear" onClick={() => setForm(f => ({ ...f, username: '' }))} tabIndex={-1}>✕</button>}
              </div>
              {errors.username && <span className="form-error">{errors.username}</span>}
            </div>

            <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
              <label className="form-label">
                Email Address <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <input
                  className="form-input"
                  type="text"
                  name="email"
                  placeholder="e.g. john@example.com"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="off"
                  disabled={submitting}
                />
                {form.email && <button type="button" className="input-clear" onClick={() => setForm(f => ({ ...f, email: '' }))} tabIndex={-1}>✕</button>}
              </div>
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className={`form-group ${errors.password ? 'has-error' : ''}`}>
              <label className="form-label">
                Password <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <input
                  className="form-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Min. 8 characters"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="input-eye"
                  onClick={() => setShowPassword(p => !p)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8
                               a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8
                               a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>

            <div className={`form-group ${errors.confirmPassword ? 'has-error' : ''}`}>
              <label className="form-label">
                Confirm Password <span className="required">*</span>
              </label>
              <div className="input-with-icon">
                <input
                  className="form-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Re-enter password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="input-eye"
                  onClick={() => setShowConfirmPassword(p => !p)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8
                               a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8
                               a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="form-error">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-input form-select"
                name="role"
                value={form.role}
                onChange={handleChange}
                disabled={submitting}
              >
                <option value="USER">User</option>
                <option value="MANAGER">Manager</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            <div className="form-group form-group-inline">
              <label className="form-label">Status</label>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={form.enabled}
                  onChange={handleChange}
                  disabled={submitting}
                />
                <span className="toggle-track">
                  <span className="toggle-thumb" />
                </span>
                <span className="toggle-text">{form.enabled ? 'Active' : 'Inactive'}</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="btn-spinner" />
                  Creating…
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  Create User
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
